import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import PredictorCard from "components/card/PredictorCard";
import { IconButton } from "@mui/material";
import { MdNavigateBefore, MdOutlineNavigateNext } from "react-icons/md";

const PredictorsCarousel = ({ slides, maxSlidesCount = 4 }) => {
  const [currentPage, setCurrentPage] = useState(0);
  const [slidesPerPage, setSlidesPerPage] = useState(1);
  const [currentSlides, setCurrentSlides] = useState([]);
  const [direction, setDirection] = useState(0); // 1 for next, -1 for previous

  // Update slides per page based on screen size
  useEffect(() => {
    const updateSlidesPerPage = () => {
      const width = window.innerWidth;
      switch (true) {
        case width >= 1440:
          setSlidesPerPage(maxSlidesCount); // Large Desktop
          break;
        case width >= 1024:
          setSlidesPerPage(3); // Desktop
          break;
        case width >= 640:
          setSlidesPerPage(2); // Tablet Portrait
          break;
        default:
          setSlidesPerPage(1); // Mobile
      }
    };

    updateSlidesPerPage();
    window.addEventListener("resize", updateSlidesPerPage);
    return () => window.removeEventListener("resize", updateSlidesPerPage);
  }, []);

  const totalPages = Math.ceil(slides.length / slidesPerPage);

  // Update current slides whenever the page or slides per page changes
  useEffect(() => {
    const start = currentPage * slidesPerPage;
    const end = start + slidesPerPage;
    setCurrentSlides(slides.slice(start, end));
  }, [currentPage, slidesPerPage, slides]);

  return (
    <div className="relative">
      <AnimatePresence mode="wait" custom={direction}>
        <motion.div
          key={currentPage}
          className="grid gap-[24px] justify-center w-full"
          style={{
            gridTemplateColumns: `repeat(${slidesPerPage}, minmax(0, 1fr))`,
          }}
          initial={(custom) => ({ opacity: 0, x: custom > 0 ? 100 : -100 })}
          animate={{ opacity: 1, x: 0 }}
          exit={(custom) => ({ opacity: 0, x: custom > 0 ? -100 : 100 })}
          transition={{
            type: "spring",
            stiffness: 260,
            damping: 40,
          }}
          custom={direction}
        >
          {currentSlides.map((predictor, index) => (
            <div key={index} className="flex-shrink-0">
              <PredictorCard predictor={predictor} />
            </div>
          ))}
        </motion.div>
      </AnimatePresence>
      <div className="flex justify-end mt-6">
        <IconButton
          onClick={() => {
            if (currentPage > 0) {
              setDirection(-1);
              setCurrentPage(currentPage - 1);
            }
          }}
          disabled={currentPage === 0}
        >
          <MdNavigateBefore
            color={currentPage > 0 ? "#6685FF" : "#D3D1CF"}
            size={40}
          />
        </IconButton>
        <IconButton
          onClick={() => {
            if (currentPage < totalPages - 1) {
              setDirection(1);
              setCurrentPage(currentPage + 1);
            }
          }}
          disabled={currentPage >= totalPages - 1}
        >
          <MdOutlineNavigateNext
            color={currentPage < totalPages - 1 ? "#6685FF" : "#D3D1CF"}
            size={40}
          />
        </IconButton>
      </div>
    </div>
  );
};

export default PredictorsCarousel;
